/** BytePlus Asset CreateAsset: image width/height must be in [300, 6000]. */
export const BYTEPLUS_ASSET_IMAGE_MIN_PX = 300

/** Uniform upscale target so both sides are ≥ minPx; null when already large enough. */
export function minImageUpscaleSize(
  width: number,
  height: number,
  minPx: number = BYTEPLUS_ASSET_IMAGE_MIN_PX,
): { width: number; height: number } | null {
  if (minPx < 1 || width < 1 || height < 1) return null
  if (width >= minPx && height >= minPx) return null

  const scale = Math.max(minPx / width, minPx / height)
  return {
    width: Math.max(minPx, Math.ceil(width * scale)),
    height: Math.max(minPx, Math.ceil(height * scale)),
  }
}

/**
 * Upscale a PNG/JPEG blob so both sides are at least `minPx` (uniform scale, no crop).
 * Leaves the blob unchanged when already large enough.
 */
export async function ensureMinImageBlob(blob: Blob, minPx: number = BYTEPLUS_ASSET_IMAGE_MIN_PX): Promise<Blob> {
  if (minPx < 1) return blob

  const bitmap = await createImageBitmap(blob)
  try {
    const target = minImageUpscaleSize(bitmap.width, bitmap.height, minPx)
    if (!target) return blob

    const canvas = document.createElement("canvas")
    canvas.width = target.width
    canvas.height = target.height
    const ctx = canvas.getContext("2d")
    if (!ctx) return blob

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = "high"
    ctx.drawImage(bitmap, 0, 0, target.width, target.height)

    const out = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"))
    return out ?? blob
  } finally {
    bitmap.close()
  }
}
